import CommonDatePicker from "@/common/elements/DatePicker";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import TimePicker from "@/common/elements/TimePicker";
import DynamicForm from "@/common/DynamicForm";
import { StyledGridCol, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import {
    getOptionsFromRefList,
    isNonEmptyVariable,
    convertApiFields,
    getSQLTypeToHTMLType,
} from "@/util/common_function";
import { Form } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { SCHEMA_NAME } from "@/util/common_variable";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";

const VictimNotificationAttemptForm = ({selectedVictim, updateFormValue, onExtraMetaChange, startingPoint}) =>{

    const { bookingsFieldsEnabled } = useSelector(
        (state) => state?.bookings
    );

    const [victimLogForm] = Form.useForm();

    // -----------------------------------------------------------------------
    // VictimNotificationLogExtra — PCI's per-client dynamic "_U" columns for
    // this notification attempt (VictimNotificationLogInterface.asp's
    // "Additional Victim Notification Information" section, shown right
    // after Comments). Reported up to the parent
    // (VictimNotificationDetails.jsx) via onExtraMetaChange, since the
    // parent — not this form — is what merges the saved values into
    // selectedRow.victimNotificationLog[].victimNotificationLogExtra[0] on
    // save (this form only streams field-by-field changes up via
    // onValuesChange, it never itself owns the save).
    // -----------------------------------------------------------------------
    const [victimLogExtraFields, setVictimLogExtraFields] = useState([]);
    const [victimLogExtraFieldsReady, setVictimLogExtraFieldsReady] = useState(false);
    const victimLogExtraTypeMapRef = useRef({});
    const victimLogExtraIdRef = useRef(0);

    useEffect(() => {
        api
            .get(`${API_ENDPOINTS.getTableColumns}/VictimNotificationLogExtra`)
            .then((response) => {
                const tableData = (response?.data || []).filter((d) => d?.columnName?.endsWith("_U"));
                const converted = convertApiFields(tableData, "VictimNotificationLogExtra");
                const typeMap = {};
                converted.forEach((c) => { typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type); });
                victimLogExtraTypeMapRef.current = typeMap;
                setVictimLogExtraFields(converted);
                setVictimLogExtraFieldsReady(true);
            })
            .catch((error) => {
                console.error("[VictimNotificationAttemptForm] VictimNotificationLogExtra field defs fetch failed:", error);
            });
    }, []);

    // Fetches the saved extra row once a real victimNotificationLogID is
    // present (edit mode) — a brand new attempt has no id yet.
    useEffect(() => {
        if (!victimLogExtraFieldsReady) return;
        const id = selectedVictim?.victimNotificationLogID;
        if (!isNonEmptyVariable(id)) {
            victimLogExtraIdRef.current = 0;
            return;
        }

        fetchExtraTableRecord("VictimNotificationLogExtra", "victimNotificationLogID", id)
            .then((result) => {
                if (!result) return;
                victimLogExtraIdRef.current = result.raw?.VictimNotificationLogExtraID ?? 0;
                const withDates = Object.keys(result.values).reduce((acc, key) => {
                    const val = result.values[key];
                    const htmlType = victimLogExtraTypeMapRef.current[key];
                    const isDateField = htmlType === "date" || htmlType === "datetime";
                    acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
                    return acc;
                }, {});
                victimLogForm.setFieldsValue(withDates);
            })
            .catch((error) => {
                console.error("[VictimNotificationAttemptForm] VictimNotificationLogExtra fetch failed:", error);
            });
    }, [selectedVictim?.victimNotificationLogID, victimLogExtraFieldsReady]);

    // Report field keys/type map/current PK up to the parent whenever they
    // change — the parent needs these at save time, not render time.
    useEffect(() => {
        onExtraMetaChange?.({
            fieldKeys: victimLogExtraFields.map((f) => f.columnName),
            typeMap: victimLogExtraTypeMapRef.current,
            extraID: victimLogExtraIdRef.current,
        });
    }, [victimLogExtraFields, selectedVictim?.victimNotificationLogID]);

    useEffect(() => {
        victimLogForm.setFieldsValue({
            dateTimeNotified: selectedVictim?.dateNotified ? dayjs(`${selectedVictim?.dateNotified} ${selectedVictim?.timeNotified}`) : dayjs(new Date()),
            comments: selectedVictim?.comments ? selectedVictim?.comments : null,
            notifyingOfficerStaffID: selectedVictim?.notifyingOfficerStaffID ? selectedVictim?.notifyingOfficerStaffID : null,
            method: selectedVictim?.method ? Number(selectedVictim?.method) : null,
            successful: selectedVictim?.successful === 'True' || selectedVictim?.successful === true,
        })
    }, [selectedVictim])

    return(
        <FieldSecurityProvider
            entity={SCHEMA_NAME.VICTIM_NOTIFICATION}
            startingPoint={startingPoint}
            bookingsFieldsEnabled={bookingsFieldsEnabled}
        >
            <>
                <Form form={victimLogForm} onValuesChange={updateFormValue}>

                    <StyledSectionHeading>
                        <span>Notification Information</span>
                    </StyledSectionHeading>

                    <StyledGridRow gutter={16} justify="end">
                        <StyledGridCol marginBottom="10px">
                            <SecureFormItem
                                fieldName="successful"
                                formItemProps={{
                                    label: "Successful?",
                                    labelPostion: "label-right",
                                    name: "successful",
                                    layout: "horizontal",
                                    className: "form-item-checkbox-left",
                                    valuePropName: "checked"
                                }}
                            >
                                <Checkbox checked={victimLogForm?.getFieldValue('successful')} />
                            </SecureFormItem>
                        </StyledGridCol>
                    </StyledGridRow>

                    <StyledGridRow gutter={16}>

                        <StyledGridCol lg={6}>
                            <SecureFormItem
                                fieldName="method"
                                formItemProps={{ label: "Method", name: "method" }}
                            >
                                <Select 
                                    options={getOptionsFromRefList("VictimNotification_Method", false, false, true)}
                                    placeholder="Method"
                                    showSearch={false}
                                    variant="outlined"
                                />
                            </SecureFormItem>
                        </StyledGridCol>

                        <StyledGridCol lg={6}>
                            <SecureFormItem
                                fieldName="notifyingOfficerStaffID"
                                formItemProps={{ label: "Notifying Officer", name: "notifyingOfficerStaffID" }}
                            >
                                <Select
                                    options={getOptionsFromRefList("", true)}
                                    placeholder="Notifying Officer"
                                    showSearch={false}
                                    onChange={(e) => {
                                        setTimeout(() => document.activeElement.blur(), 0)
                                    }}
                                    variant="outlined"
                                />
                            </SecureFormItem>
                        </StyledGridCol>

                        <StyledGridCol lg={6}>
                            <SecureFormItem
                                fieldName="dateTimeNotified"
                                formItemProps={{ label: "Date Notified", name: "dateTimeNotified" }}
                            >
                                <CommonDatePicker showTime format="MM/DD/YYYY HH:mm" />
                            </SecureFormItem>
                        </StyledGridCol>

                        <StyledGridCol lg={24} span={24}>
                            <SecureFormItem
                                fieldName="comments"
                                formItemProps={{ label: "Comments", name: "comments" }}
                            >
                                <TextField inputType="textarea" rows="3" />
                            </SecureFormItem>
                        </StyledGridCol>

                    </StyledGridRow>

                    {victimLogExtraFields.length > 0 && (
                        <>
                            <StyledSectionHeading>
                                <span>Additional Victim Notification Information</span>
                            </StyledSectionHeading>
                            <StyledGridRow gutter={16}>
                                {victimLogExtraFields.map((sf, index) => (
                                    <DynamicForm
                                        key={index}
                                        formAttributes={sf}
                                        form={victimLogForm}
                                        bookingsFieldsEnabled={bookingsFieldsEnabled}
                                    />
                                ))}
                            </StyledGridRow>
                        </>
                    )}

                </Form>
            </>
        </FieldSecurityProvider>
    )
}

export default VictimNotificationAttemptForm;